import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getGeolocationCoordinates} from 'modules/meta/selectors'
import {ParamsInvalidError} from 'modules/sapiSearch/errors'
import {useHandleSearchError} from 'modules/sapiSearch/SapiProvider'
import {syncSearchParameters} from 'modules/sapiSearch/slice'
import {
  getIsBoVioEnvironment,
  getSearchHasBookingHint
} from 'modules/search/selectors'
import {syncStoreWithUrl} from 'modules/search/slice'
import {SearchUrlParams} from 'modules/search/types'
import {useRouter} from 'next/router'
import {
  appendPathParamsToSearchParams,
  getSerializedUrlParams,
  isValidSearchUrlParams
} from 'utils/searchParams'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {getAllCookies} from '@vio/core/src/_web/utils/cookies'
import {getEmailDomain} from '@vio/core/src/auth/business/authentication'
import {
  selectIsAuthenticated,
  selectIsEmployee,
  selectIsLoading,
  selectIsMemberPlus,
  selectUserEmail,
  selectUserId
} from '@vio/core/src/auth/modules/selectors'
import {getDateValidationReason} from '@vio/core/src/searchParams/validateSearchParamDates'

import {hasStickyOtaMode} from './useOtaMode/sessionStore'

interface Props {
  disallowInvalidSearchParams?: boolean
}

/**
 * Hook used to sync the store with URL params
 *
 * This is mirrored by `preloadBaseSearchState` server-side
 */
export const useSyncStoreWithUrl = ({
  disallowInvalidSearchParams = false
}: Props = {}) => {
  const dispatch = useDispatch()
  const handleSearchError = useHandleSearchError()
  const {query} = useRouter()
  const {isMobile} = useDeviceLayout()
  const isEmployee = useSelector(selectIsEmployee)
  const isMemberPlus = useSelector(selectIsMemberPlus)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isAuthLoading = useSelector(selectIsLoading)
  const email = useSelector(selectUserEmail)
  const emailDomain = getEmailDomain(email)
  const userId = useSelector(selectUserId)
  const hasBookingHint = useSelector(getSearchHasBookingHint)
  const geolocation = useSelector(getGeolocationCoordinates)
  const stringifiedGeolocation = JSON.stringify(geolocation)

  // Merge path params and produce a string for the useEffect dependency
  const {queryString: queryStringOriginal} = useRouterProvider()
  const {queryString} = appendPathParamsToSearchParams(
    new URLSearchParams(queryStringOriginal),
    query
  )

  const isBoVioEnvironment = useSelector(getIsBoVioEnvironment)

  useEffect(() => {
    const urlParams = getSerializedUrlParams(
      queryString
    ) as Partial<SearchUrlParams>

    if (disallowInvalidSearchParams && !isValidSearchUrlParams(urlParams)) {
      handleSearchError(new ParamsInvalidError())
      return
    }

    const dateValidationReason = getDateValidationReason(urlParams)

    dispatch(
      syncStoreWithUrl({
        queryString,
        urlParams,
        isMobile,
        isSearchBoxOpenByDefault: hasBookingHint,
        dateValidationReason
      })
    )
  }, [
    dispatch,
    queryString,
    isMobile,
    hasBookingHint,
    handleSearchError,
    disallowInvalidSearchParams
  ])

  // Location and account changes refresh SAPI without resyncing the previous URL during navigation.
  useEffect(() => {
    const urlParams = getSerializedUrlParams(
      queryString
    ) as Partial<SearchUrlParams>

    if (disallowInvalidSearchParams && !isValidSearchUrlParams(urlParams))
      return

    dispatch(
      syncSearchParameters({
        queryParams: new URLSearchParams(queryString),
        cookies: getAllCookies(),
        geolocation: urlParams.userLocationSearch ? geolocation : undefined,
        user: {
          isAuthLoading,
          isEmployee,
          isMemberPlus,
          isAuthenticated,
          emailDomain,
          userId
        },
        taxonomy: query?.taxonomy,
        // Read sessionStorage directly: Redux can be transiently wrong
        // between hydration (cookie-derived) and post-hydration
        // reconciliation. sessionStorage is the tab-scoped source of truth.
        isStickyOtaMode: hasStickyOtaMode()
      })
    )
  }, [
    dispatch,
    isAuthLoading,
    queryString,
    isMemberPlus,
    isEmployee,
    isAuthenticated,
    emailDomain,
    userId,
    stringifiedGeolocation,
    disallowInvalidSearchParams,
    isBoVioEnvironment
  ])
}
